<template>
	<AppCard class="card-margin" :title="getString('x_service_settings', [userStore.takeawayWord])" :button-text="getString('save')" @button-click="saveSettings">
		<template v-slot:body>
			<AppList :items="statusItems">
				<template #number-input-slot="{item}">
					<div class="row centered space-between">
						<p class="number-p">{{item.title}}</p>
						<AppNumberInput class="input-item" :min="0" :rightSideSymbol="item.rightSymbol" :symbol="item.symbol || ''" :value="item.value" @changed-value="v=>{item.valueCallback(v)}" />
					</div>
				</template>
				<template #amount-input-slot="{item}">
					<div class="row centered space-between">
						<p class="number-p">{{item.title}}</p>
						<AppNumberInput doubleDecimal class="input-item" :min="0" :rightSideSymbol="item.rightSymbol" :symbol="item.symbol || ''" :value="amountItemValue(item)" @changed-value="v=>{item.valueCallback(v)}" />
					</div>
				</template>
				<template #timetables="{item}">
					<div class="row centered space-between">
						<p class="button-p">{{item.title}}</p>
						<AppButton class="input-item" small @click="item.callback" outline :text="getString('set_hours')"/>
					</div>
				</template>
			</AppList>
		</template>
	</AppCard>
	<AppBottomSheet :force-width="bottomSheetWidth" :active="timetablesSheetActive" @toggling-popup="closeTimetablesSheet">
		<div class="timetables-content" v-if="timetablesSheetActive">
            <div class="row centered">
                <AppLoader v-if="loadingTimetables" primary />
            </div>
            <TimetablesEditor takeaway v-if="!loadingTimetables && loadedTimetables" :title="getString('x_timetables', [userStore.takeawayWord])" with-quantity with-intervals :timetables="timetables" @save-clicked="closeTimetablesSheet" />
        </div>
	</AppBottomSheet>
	<AppPopup ref="popup-other-payments" prevent-overlay-dismiss :title="getString('enable_other_payments_methods')" :buttons="loadingOtherPayments ? [] : [{text: getString('cancel'), negative: true, callback: cancelSubOtherPayments}, {text: getString('continue'), callback: subOtherPaymentsMethod}]">
		<div v-if="loadingOtherPayments" class="row centered loader-container"><AppLoader primary /></div>
		<p v-if="!loadingOtherPayments" class="popup-body-text">{{ getString(pendingSubscriptionType === 'takeaway_cash' ? 'enable_other_payments_methods_explain' : 'enable_other_payments_methods_sms_explain') }}</p>
	</AppPopup>
	<CheckoutInPlace :client-secret="clientSecret" @toggling-popup="togglingPopupSheet" @checkout-complete="paymentComplete" />
</template>

<script>
import AppCard from "@/shared/components/AppCard.vue";
import AppList from "@/shared/components/AppList.vue";
import AppPopup from "@/shared/components/AppPopup.vue";
import AppChevronIcon from "@/shared/components/AppChevronIcon.vue";
import AppButton from "@/shared/components/AppButton.vue";
import AppSwitch from "@/shared/components/AppSwitch.vue";
import AppNumberInput from "@/shared/components/AppNumberInput.vue";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import AppLoader from "@/shared/components/AppLoader.vue";
import TimetablesEditor from "@/console-new/components/shared/TimetablesEditor.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";

import {useCurrencyStore, useLanguageStore, useNotificationStore} from "@/shared/stores";
import {useUserStore} from "@/console-new/stores";
import CheckoutInPlace from "@/console-new/components/shared/CheckoutInPlace.vue";
export default {
	setup() {
		let languageStore = useLanguageStore();
		let userStore = useUserStore();
		const notificationStore = useNotificationStore();
		const currencyStore = useCurrencyStore();
		return {getString: languageStore.getString, showNotification: notificationStore.showNotification, currencyStore, userStore}
	},
	name: "TakeawaySettingsCard",
	mixins: [WindowWidth],
	emits: ['save-settings'],
	components: {
		CheckoutInPlace,
		AppCard, AppList, AppNumberInput, AppPopup, AppBottomSheet, AppLoader, TimetablesEditor, AppButton, AppSwitch, AppChevronIcon},
	data() {
		return {
			editedSettings: false,
			timetablesSheetActive: false,
			loadingTimetables: false,
			loadedTimetables: false,
			timetables: [],
			loadingOtherPayments: false,
			clientSecret: null,
			pendingSubscriptionType: null, // 'takeaway_cash' or 'takeaway_enabled_sms'
		}
	},
	computed: {
		statusItems() {
			let smsPriceString = this.currencyStore.readableCurrencyValue(this.currencyStore.currCurrency.sms_fee);

			return [
				{
					title: this.getString('takeaway_timetables'),
					customSlot: 'timetables',
					callback: this.openTimetables
				}, {
					title: this.getString('cash_payment'),
					switch: true,
					active: this.takeawayCash || 0,
					callback: this.changeTakeawayCash,
				}, {
					title: this.getString("sms_whatsapp_notifications"),
					switch: true,
					active: this.takeawayEnabledSms,
					explain: this.getString("sms_whatsapp_notifications_explain", [smsPriceString]),
					callback: this.changeTakeawayEnabledSms,
				}, {
					title: this.getString('additional_costs_currency', [this.currencyStore.currCurrency.symbol]),
					customSlot: 'amount-input-slot',
					value: this.takeawayCosts,
					valueCallback: (newStatus) => {
						this.editedSettings = true;
						this.userStore.currBusiness.takeaway_costs = this.currencyStore.actualToDbValue(newStatus);
					},
				}, {
					title: this.getString('min_order_currency', [this.currencyStore.currCurrency.symbol]),
					customSlot: 'amount-input-slot',
					value: this.takeawayMinOrder || 0,
					valueCallback: (newStatus) => {
						this.editedSettings = true;
						this.userStore.currBusiness.takeaway_min_order = this.currencyStore.actualToDbValue(newStatus);
					},
				}, {
					title: this.getString('scheduled_days_advance'),
					customSlot: 'number-input-slot',
					value: this.takeawayFutureDays || 0,
					valueCallback: (newStatus) => {
						this.editedSettings = true;
						this.userStore.currBusiness.takeaway_future_days = newStatus;
					},
				},

			]
		},
		takeawayCash() {
			return this.userStore.currBusiness ? this.userStore.currBusiness.takeaway_cash : false;
		},
		takeawayEnabledSms() {
			return this.userStore.currBusiness && this.userStore.currBusiness.takeaway_enabled_sms;
		},
		otherPaymentsActive() {
			return this.userStore.currBusiness ? this.userStore.currBusiness.sub_otherpayments_id : false;
		},
		takeawayCosts() {
			return this.userStore.currBusiness ? this.userStore.currBusiness.takeaway_costs : null;
		},
		takeawayMinOrder() {
			return this.userStore.currBusiness ? this.userStore.currBusiness.takeaway_min_order : null;
		},
		takeawayFutureDays() {
			return this.userStore.currBusiness ? this.userStore.currBusiness.takeaway_future_days : null;
		},
		bottomSheetWidth() {
			if(this.windowWidth < 550) {
				return "100%";
			} else if(this.windowWidth < 768) {
				return "550px";
			} else if(this.windowWidth < 1080) {
				return "720px";
			} else if(this.windowWidth < 1440) {
				return "960px";
			} else {
				return "1280px";
			}
		}
	},
	methods: {
		amountItemValue(item) {
			return this.currencyStore.dbToActualValue(item.value);
		},
		togglingPopupSheet(v) {
			if(!v) {
				this.clientSecret = null;
			}
		},
		paymentComplete() {
			setTimeout(() => {
				this.userStore.refreshBusiness(this.userStore.currBusinessId);
			}, 2000);
			setTimeout(() => {
				this.clientSecret = null;
				if(this.pendingSubscriptionType) {
					this.userStore.currBusiness[this.pendingSubscriptionType] = true;
					this.pendingSubscriptionType = null;
				}
			}, 2500);
		},
		closeTimetablesSheet(open) {
			if(!open) {
				this.timetablesSheetActive = false;
				this.loadedTimetables = false;
			}
		},
		openTimetables() {
			this.timetablesSheetActive = true;
			this.loadingTimetables = true;
			this.loadedTimetables = false;
			this.userStore.getTakeawayTimetables(timetables => {
				this.loadingTimetables = false;
				this.loadedTimetables = true;
				this.timetables = timetables;
			});
		},
		changeTakeawayOtherPaymentsFeature(feature, newStatus) {
			this.editedSettings = true;
			this.userStore.currBusiness[feature] = !!newStatus;
			requestAnimationFrame(() => {
				if (!!newStatus && !this.otherPaymentsActive) {
					this.userStore.currBusiness[feature] = false;
					this.pendingSubscriptionType = feature;
					this.$refs["popup-other-payments"].toggleStatus();
				}
			});
		},
		changeTakeawayCash(newStatus) {
			this.changeTakeawayOtherPaymentsFeature('takeaway_cash', newStatus);
		},
		changeTakeawayEnabledSms(newStatus) {
            this.changeTakeawayOtherPaymentsFeature('takeaway_enabled_sms', newStatus);
		},
		cancelSubOtherPayments() {
			if(this.pendingSubscriptionType) {
				this.userStore.currBusiness[this.pendingSubscriptionType] = false;
			}
			this.pendingSubscriptionType = null;
			this.$refs["popup-other-payments"].toggleStatus(false);
		},
		subOtherPaymentsMethod() {
			if(!this.pendingSubscriptionType) {
				return;
			}
			this.loadingOtherPayments = true;
			let dataAction = this.pendingSubscriptionType === 'takeaway_cash' ? 'takeaway-cash' : 'takeaway-sms';
			this.userStore.subOtherPaymentsMethods(dataAction, (success, details) => {
				this.$refs["popup-other-payments"].toggleStatus(false);
				this.loadingOtherPayments = false;
				if(!success) {
					if(details && details === 'currency_mismatch') {
						this.showNotification("user-currency-mismatch", [this.userStore?.user?.name]);
					} else {
						this.showNotification("generic-fail");
					}
				} else {
					this.clientSecret = success;
				}
			});
		},
		saveSettings() {
			if(!this.editedSettings) {
				this.showNotification("generic-success");
			} else {
				this.$emit('save-settings');
			}
		}
	},
	watch: {
		'userStore.currBusinessId': function (newVal, oldVal) {
			if (newVal !== null && newVal !== oldVal) {
				this.editedSettings = false;
			}
		}
	}
}
</script>

<style lang="scss" scoped>
@use "@/console-new/assets/scss/_variables.scss" as *;

.card-margin {
	margin-top: $mainPadding;
}
.extra-text {
	width: 100%;
	padding-top: 10px;
	transition: opacity 0.3s, max-height .3s, padding-top .3s;
	max-height: 200px;
	p {
		font-size: 14px;
		color: #9B9B9B;
	}
	&.hide {
		opacity: 0;
		max-height: 0;
		padding-top: 0;
	}
}
.number-p {
	width: calc(100% - 130px);
	@media (max-width: 768px) {
		width: calc(100% - 110px);
	}
}
.switch-p {
	width: calc(100% - 110px);
	@media (max-width: 768px) {
		width: calc(100% - 80px);
	}
}
.button-p {
	width: calc(100% - 120px);
}
.input-item {
	@media (max-width: 350px) {
		margin-left: auto;
	}
}
.switch-p, .number-p, .button-p {
	@media (max-width: 600px) {
		font-size: 14px;
	}
	@media (max-width: 400px) {
		font-size: 12px;
	}
	@media (max-width: 350px) {
		width: 100%;
		margin-bottom: 6px;
	}
}
</style>